import type { BackendUser } from '../../../services/user/userService';
import { useRestaurantStore } from '../../../stores/appRestaurantStore';
import { useCostPriceStore } from '../../../stores/appCostPriceStore';
import { useCostPriceCategoriesStore } from '../../../stores/appCostPriceCategoriesStore';
import { useOrderHistory } from '../../../stores/appOrderHistoryStore';
import { useRevenueHistory } from '../../../stores/appRevenueHistoryStore';
import { useSubrecipesStore } from '../../../stores/appSubrecipesStore';
import { useSourceRestaurantDataStore } from '../../../stores/appSourceRestaurantDataStore';
import { collectSourceRestaurantIds } from '../../../utils/costPriceUtils';

// The shared drawer calculates immediately from global stores. Do not mount it
// until every input belongs to the selected restaurant, including after a failed load.
export async function loadSetupEditorData(restaurantId: string, backendUser: BackendUser | null, isCurrent: () => boolean) {
  const restaurants = useRestaurantStore.getState();
  if (!isCurrent() || !restaurants.isDataLoaded) throw new Error('Restaurant data unavailable');
  const platformIds = (restaurants.restaurantSections[restaurantId] ?? [])
    .flatMap(section => (restaurants.restaurantSectionPlatforms[section.id] ?? []).map(platform => platform.id)).sort();
  const sourceIds = (restaurants.restaurantRevenueSources[restaurantId] ?? []).map(source => source.id).sort();
  const orders = useOrderHistory.getState();
  const revenue = useRevenueHistory.getState();
  const prices = useCostPriceStore.getState();
  const categoryData = useCostPriceCategoriesStore.getState();
  const subrecipes = useSubrecipesStore.getState();
  await Promise.all([
    (!prices.isDataLoadedFor(restaurantId) || prices.isLoading) && prices.load(restaurantId, { force: true, isCurrent }),
    (!categoryData.isDataLoadedFor(restaurantId) || categoryData.error || categoryData.isLoading) && categoryData.load(restaurantId, { force: true }),
    (!prices.isCustomIngredientsLoadedFor(restaurantId) || prices.customIngredientsError || prices.isLoadingCustomIngredients) &&
      prices.loadCustomIngredients(restaurantId, { force: true }),
    (!subrecipes.isDataLoadedFor(restaurantId) || subrecipes.isLoading) && subrecipes.load(restaurantId, { force: true, isCurrent }),
    platformIds.length ? orders.loadByPlatformIds(restaurantId, platformIds, restaurants.platformToSectionIndex, backendUser)
      : orders.markOrderhistoryEmptyForRestaurant(restaurantId),
    sourceIds.length ? revenue.loadByRevenueSourceIds(restaurantId, sourceIds, restaurants.restaurantRevenueSources)
      : revenue.markRevenuehistoryEmptyForRestaurant(restaurantId),
  ]);
  if (!isCurrent()) throw new Error('Editor context changed');
  const cost = useCostPriceStore.getState();
  const categories = useCostPriceCategoriesStore.getState();
  const loadedOrders = useOrderHistory.getState();
  const loadedRevenue = useRevenueHistory.getState();
  if (!cost.isDataLoadedFor(restaurantId) || !cost.isCustomIngredientsLoadedFor(restaurantId) || cost.customIngredientsError ||
      !categories.isDataLoadedFor(restaurantId) || categories.error || !useSubrecipesStore.getState().isDataLoadedFor(restaurantId) ||
      !loadedOrders.isDataLoadedForPlatforms(platformIds) || !loadedRevenue.isDataLoadedForRevenueSources(sourceIds)) {
    throw new Error('Cost-price data unavailable');
  }
  const siblings = collectSourceRestaurantIds(cost.costPriceList, restaurantId);
  await Promise.all(siblings.map(id => useSourceRestaurantDataStore.getState().ensureLoaded(id)));
  const sources = useSourceRestaurantDataStore.getState();
  if (!isCurrent() || siblings.some(id => !sources.dataByRestaurant[id] || sources.errorByRestaurant[id])) {
    throw new Error('Source restaurant data unavailable');
  }
}
